import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { cn } from "@aw/lib"

const notificationVariants = cva(
    "flex flex-col px-3 py-2 rounded-lg font-karla min-h-[28px] min-w-[256px] overflow-clip",
    {
        variants: {
            variant: {
                success: "bg-mantis-100 text-mantis-700 dark:bg-card dark:text-success",
                warning: "bg-yellow-100 text-orange-800 dark:bg-card dark:text-warning",
                error: "bg-red-50 text-red-500 dark:bg-card dark:text-danger",
                info: "bg-electric-blue-50 text-electric-blue-800 dark:bg-card dark:text-info"
            },
            size: {
                default: "",
                large: "text-base p-5"
            }
        },
        defaultVariants: {
            variant: "info",
            size: "default"
        }
    }
)

const iconVariants = cva(
    "flex-shrink-0 size-4 p-0.5",
    {
        variants: {
            variant: {
                success: "text-mantis-500",
                warning: "text-yellow-500",
                error: "text-red-500",
                info: "text-electric-blue-500"
            },
            size: {
                default: "",
                large: "size-5"
            }
        },
        defaultVariants: {
            variant: "info",
            size: "default"
        }
    }
)

type IconProps = {
    className?: string
} & React.SVGProps<SVGSVGElement>

const SuccessIcon: React.FC<IconProps> = ({ className, ...props }) => (
    <svg className={className} width="12" height="12" viewBox="0 0 12 12" fill="none" {...props}>
        <path d="M5.71406 7.96406C5.45859 8.21953 5.04141 8.21953 4.78594 7.96406L3.28594 6.46406C3.03047 6.20859 3.03047 5.79141 3.28594 5.53594C3.54141 5.28047 3.95859 5.28047 4.21406 5.53594L5.25 6.57187L7.78594 4.03594C8.04141 3.78047 8.45859 3.78047 8.71406 4.03594C8.96953 4.29141 8.96953 4.70859 8.71406 4.96406L5.71406 7.96406ZM12 6C12 9.31406 9.31406 12 6 12C2.68594 12 0 9.31406 0 6C0 2.68594 2.68594 0 6 0C9.31406 0 12 2.68594 12 6ZM6 1.125C3.30703 1.125 1.125 3.30703 1.125 6C1.125 8.69297 3.30703 10.875 6 10.875C8.69297 10.875 10.875 8.69297 10.875 6C10.875 3.30703 8.69297 1.125 6 1.125Z" className="dark:fill-success" fill="#36672C" />
    </svg>

)

const WarningIcon: React.FC<IconProps> = ({ className, ...props }) => (
    <svg className={className} width="12" height="11" viewBox="0 0 12 11" fill="none" {...props}>
        <path d="M5.82187 1.47578C5.85938 1.4125 5.92734 1.375 6 1.375C6.07266 1.375 6.14062 1.4125 6.17813 1.47578L10.8258 9.10938C10.8586 9.16328 10.875 9.22422 10.875 9.28516C10.875 9.47266 10.7227 9.625 10.5352 9.625H1.46484C1.27734 9.625 1.125 9.47266 1.125 9.28516C1.125 9.22188 1.14141 9.16094 1.17422 9.10938L5.82187 1.47578ZM4.86094 0.889844L0.213281 8.52344C0.0726563 8.75313 0 9.01562 0 9.28516C0 10.0938 0.65625 10.75 1.46484 10.75H10.5352C11.3438 10.75 12 10.0938 12 9.28516C12 9.01562 11.925 8.75313 11.7867 8.52344L7.13906 0.889844C6.89766 0.49375 6.46641 0.25 6 0.25C5.53359 0.25 5.10234 0.49375 4.86094 0.889844ZM6.75 8.125C6.75 7.92609 6.67098 7.73532 6.53033 7.59467C6.38968 7.45402 6.19891 7.375 6 7.375C5.80109 7.375 5.61032 7.45402 5.46967 7.59467C5.32902 7.73532 5.25 7.92609 5.25 8.125C5.25 8.32391 5.32902 8.51468 5.46967 8.65533C5.61032 8.79598 5.80109 8.875 6 8.875C6.19891 8.875 6.38968 8.79598 6.53033 8.65533C6.67098 8.51468 6.75 8.32391 6.75 8.125ZM6.5625 3.8125C6.5625 3.50078 6.31172 3.25 6 3.25C5.68828 3.25 5.4375 3.50078 5.4375 3.8125V6.0625C5.4375 6.37422 5.68828 6.625 6 6.625C6.31172 6.625 6.5625 6.37422 6.5625 6.0625V3.8125Z" className="dark:fill-warning" fill="#9B8629" />
    </svg>
)

const ErrorIcon: React.FC<IconProps> = ({ className, ...props }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none"  {...props}>
        <path d="M6 1.625C7.29293 1.625 8.53291 2.13861 9.44715 3.05285C10.3614 3.96709 10.875 5.20707 10.875 6.5C10.875 7.79293 10.3614 9.03291 9.44715 9.94715C8.53291 10.8614 7.29293 11.375 6 11.375C4.70707 11.375 3.46709 10.8614 2.55285 9.94715C1.63861 9.03291 1.125 7.79293 1.125 6.5C1.125 5.20707 1.63861 3.96709 2.55285 3.05285C3.46709 2.13861 4.70707 1.625 6 1.625ZM6 12.5C7.5913 12.5 9.11742 11.8679 10.2426 10.7426C11.3679 9.61742 12 8.0913 12 6.5C12 4.9087 11.3679 3.38258 10.2426 2.25736C9.11742 1.13214 7.5913 0.5 6 0.5C4.4087 0.5 2.88258 1.13214 1.75736 2.25736C0.632141 3.38258 0 4.9087 0 6.5C0 8.0913 0.632141 9.61742 1.75736 10.7426C2.88258 11.8679 4.4087 12.5 6 12.5ZM6 3.5C5.68828 3.5 5.4375 3.75078 5.4375 4.0625V6.6875C5.4375 6.99922 5.68828 7.25 6 7.25C6.31172 7.25 6.5625 6.99922 6.5625 6.6875V4.0625C6.5625 3.75078 6.31172 3.5 6 3.5ZM6.75 8.75C6.75 8.55109 6.67098 8.36032 6.53033 8.21967C6.38968 8.07902 6.19891 8 6 8C5.80109 8 5.61032 8.07902 5.46967 8.21967C5.32902 8.36032 5.25 8.55109 5.25 8.75C5.25 8.94891 5.32902 9.13968 5.46967 9.28033C5.61032 9.42098 5.80109 9.5 6 9.5C6.19891 9.5 6.38968 9.42098 6.53033 9.28033C6.67098 9.13968 6.75 8.94891 6.75 8.75Z" className="dark:fill-danger" fill="#BF2232" />
    </svg>
)

const InfoIcon: React.FC<IconProps> = ({ className, ...props }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none" {...props}>
        <path d="M6 1.375C7.29293 1.375 8.53291 1.88861 9.44715 2.80285C10.3614 3.71709 10.875 4.95707 10.875 6.25C10.875 7.54293 10.3614 8.78291 9.44715 9.69715C8.53291 10.6114 7.29293 11.125 6 11.125C4.70707 11.125 3.46709 10.6114 2.55285 9.69715C1.63861 8.78291 1.125 7.54293 1.125 6.25C1.125 4.95707 1.63861 3.71709 2.55285 2.80285C3.46709 1.88861 4.70707 1.375 6 1.375ZM6 12.25C7.5913 12.25 9.11742 11.6179 10.2426 10.4926C11.3679 9.36742 12 7.8413 12 6.25C12 4.6587 11.3679 3.13258 10.2426 2.00736C9.11742 0.882141 7.5913 0.25 6 0.25C4.4087 0.25 2.88258 0.882141 1.75736 2.00736C0.632141 3.13258 0 4.6587 0 6.25C0 7.8413 0.632141 9.36742 1.75736 10.4926C2.88258 11.6179 4.4087 12.25 6 12.25ZM5.0625 8.125C4.75078 8.125 4.5 8.37578 4.5 8.6875C4.5 8.99922 4.75078 9.25 5.0625 9.25H6.9375C7.24922 9.25 7.5 8.99922 7.5 8.6875C7.5 8.37578 7.24922 8.125 6.9375 8.125H6.75V6.0625C6.75 5.75078 6.49922 5.5 6.1875 5.5H5.0625C4.75078 5.5 4.5 5.75078 4.5 6.0625C4.5 6.37422 4.75078 6.625 5.0625 6.625H5.625V8.125H5.0625ZM6 4.75C6.19891 4.75 6.38968 4.67098 6.53033 4.53033C6.67098 4.38968 6.75 4.19891 6.75 4C6.75 3.80109 6.67098 3.61032 6.53033 3.46967C6.38968 3.32902 6.19891 3.25 6 3.25C5.80109 3.25 5.61032 3.32902 5.46967 3.46967C5.32902 3.61032 5.25 3.80109 5.25 4C5.25 4.19891 5.32902 4.38968 5.46967 4.53033C5.61032 4.67098 5.80109 4.75 6 4.75Z" className="dark:fill-info" fill="#005A64" />
    </svg>
)

const CloseIcon: React.FC<IconProps> = ({ className, ...props }) => (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" {...props}>
        <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
    </svg>
)

interface NotificationProps extends
    Omit<React.ComponentProps<"div">, "color">,
    VariantProps<typeof notificationVariants> {
    title?: string | null
    description?: string | React.ReactNode
    onClose?: () => void
    icon?: boolean
    children?: React.ReactNode
    testId?: string
}

function Notification({
    className,
    variant = "info",
    size = "default",
    title,
    description,
    onClose,
    icon = true,
    children,
    testId,
    ...props
}: NotificationProps) {
    const getIcon = () => {
        switch (variant) {
            case "success":
                return SuccessIcon
            case "warning":
                return WarningIcon
            case "error":
                return ErrorIcon
            case "info":
            default:
                return InfoIcon
        }
    }

    const getTitle = () => {
        switch (variant) {
            case "success":
                return "Success"
            case "warning":
                return "Warning"
            case "error":
                return "Error"
            case "info":
            default:
                return "Info"
        }
    }

    const getDefaultDescription = () => {
        switch (variant) {
            case "success":
                return "Your action was successful."
            case "warning":
                return "There might be potential issues."
            case "error":
                return "Something went wrong."
            case "info":
            default:
                return "Here is some information."
        }
    }

    const IconComponent = getIcon()
    const displayTitle = title !== undefined ? title : getTitle()
    const hasTitle = displayTitle !== null && displayTitle !== ""
    const body = children ? children : description || getDefaultDescription()

    return (
        <div
            className={cn(notificationVariants({ variant, size, className }), hasTitle && "gap-2")}
            role="alert"
            data-testid={testId}
            {...props}
        >
            {hasTitle ? (
                <>
                    <div className="flex items-center gap-1">
                        {icon && (
                            <IconComponent className={iconVariants({ variant, size })} />
                        )}
                        <span className="font-bold text-base leading-[16px]">
                            {displayTitle}
                        </span>
                        {onClose && (
                            <button
                                onClick={onClose}
                                className="flex-shrink-0 ml-auto p-1 cursor-pointer rounded hover:bg-black/5 transition-colors"
                                aria-label="Close notification"
                            >
                                <CloseIcon className="size-4" />
                            </button>
                        )}
                    </div>
                    <div
                        className="text-sm leading-4.5 font-normal first-letter:capitalize text-foreground w-full"
                        data-testid={testId ? `${testId}-message` : undefined}
                    >
                        {body}
                    </div>
                </>
            ) : (
                <div className="flex items-start gap-1">
                    {icon && (
                        <div className="flex items-center shrink-0 h-[12px]">
                            <IconComponent className={iconVariants({ variant, size })} />
                        </div>
                    )}
                    <p
                        className="flex-1 min-w-0 text-sm leading-4.5 font-normal first-letter:capitalize text-foreground"
                        data-testid={testId ? `${testId}-message` : undefined}
                    >
                        {body}
                    </p>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="flex-shrink-0 ml-auto p-1 cursor-pointer rounded hover:bg-black/5 transition-colors"
                            aria-label="Close notification"
                        >
                            <CloseIcon className="size-4" />
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

export default Notification
export { notificationVariants, type NotificationProps }